import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {FormArray, FormsModule} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {Subscription} from 'rxjs';
import {startWith} from 'rxjs/operators';
import {Field} from '@app/interfaces/entities/field';
import {FieldHelperService} from '@app/services/util/field-helper.service';
import {FormHelperService} from '@app/util/form-helper.service';
import {ConfirmDialogComponent, DialogType} from '@app/shared/dialogs';
import {V2ExpenseFormDialogComponent} from '@app/modules/vehicles/vehicle-page/tabs/v2-procurement-tab/v2-expense-form-dialog/v2-expense-form-dialog.component';

export interface CostFileLink {
  url: string;
  fileName: string;
}

export interface CostRow {
  itemValue: any;
  uuid: string;
  name: string;
  amount: number;
  date: string | null;
  files: CostFileLink[];
  /** Free-text note, shown under the name like the legacy table's `comment`. */
  comment: string | null;
  /** false → the row came from the income repeater ("priliv"). */
  isExpense: boolean;
  /** The repeater this row lives in — edit/delete write it back. */
  repeater: Field<string>;
  /** Form mode: the row fails its sub-field validators (e.g. legacy data). */
  invalid: boolean;
}

/** Sub-field type-ids differ per repeater; everything else is symmetric. */
export const COST_REPEATERS = {
  expense: {
    repeaterId: 'briefd.vehicle.expenses.expenses_repeater',
    title: 'briefd.vehicle.expenses.expense_title',
    amount: 'briefd.vehicle.expenses.expense_amount',
    date: 'briefd.vehicle.expenses.expense_date',
    files: 'briefd.vehicle.expenses.expense_files',
    comment: 'briefd.vehicle.expenses.expense_comment',
  },
  income: {
    repeaterId: 'briefd.vehicle.expenses.income_repeater',
    title: 'briefd.vehicle.expenses.income_title',
    amount: 'briefd.vehicle.expenses.income_amount',
    date: 'briefd.vehicle.expenses.income_date',
    files: 'briefd.vehicle.expenses.income_files',
    comment: 'briefd.vehicle.expenses.income_comment',
  },
} as const;

/**
 * Design 2.0 "Stroški in prilivi" card, shared by the vehicle page's Nabava
 * tab and the vehicle form (BRIEFD-7558).
 *
 * Two modes:
 *  - `control` unset (Nabava tab): rows come from the `expenseRepeater` /
 *    `incomeRepeater` inputs and add/edit/remove are emitted — the host
 *    persists them immediately.
 *  - `control` set (vehicle form): the card edits that FormArray in place, so
 *    costs are saved with the rest of the form on Shrani. Patching the API
 *    directly would be overwritten by the form's own stale copy of the
 *    repeater, and a new vehicle has no uuid to patch yet.
 */
@Component({
  selector: 'app-v2-costs-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './v2-costs-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2CostsCardComponent implements OnChanges {
  @Input() expenseRepeater: Field<string> | null = null;
  @Input() incomeRepeater: Field<string> | null = null;
  /** Show the income half. Explicit rather than `!!incomeRepeater` so the
   *  header doesn't change shape while the repeaters are still loading. */
  @Input() withIncome = false;
  @Input() isLoading = false;
  /** Form mode — the vehicle form's own control for `expenseRepeater`. */
  @Input() control: FormArray | null = null;

  @Output() add = new EventEmitter<Field<string>>();
  @Output() edit = new EventEmitter<CostRow>();
  @Output() remove = new EventEmitter<CostRow>();

  expenses: CostRow[] = [];
  expensesTotal = 0;
  incomes: CostRow[] = [];
  incomesTotal = 0;
  /** Legacy Kalkulacija filter: '' = vse, 'cost' = samo stroški, 'income' = samo prilivi. */
  rowFilter: '' | 'cost' | 'income' = '';
  /** Legacy table sort (matSort on amount / createdAt). null = unsorted, which
   *  keeps the repeater's own order — the same three-state toggle v1 had. */
  sortColumn: 'amount' | 'date' | null = null;
  sortAsc = true;
  detailsOpen = true;

  public readonly expenseFallbackLabel = $localize`Strošek`;
  public readonly incomeFallbackLabel = $localize`Priliv`;
  public readonly editExpenseAriaLabel = $localize`Uredi strošek`;
  public readonly deleteExpenseAriaLabel = $localize`Izbriši strošek`;

  private fieldHelper = inject(FieldHelperService);
  private formHelper = inject(FormHelperService);
  private matDialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private controlSub?: Subscription;

  ngOnChanges(): void {
    this.controlSub?.unsubscribe();
    if (!this.control) {
      this.buildRows();
      return;
    }
    this.controlSub = this.control.valueChanges
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildRows();
        this.cdr.markForCheck();
      });
  }

  /** The vehicle form disables itself while saving and for deleted vehicles;
   *  a row written then would be lost or unlock the read-only form. */
  get isLocked(): boolean {
    return !!this.control?.disabled;
  }

  onAdd(repeater: Field<string> | null): void {
    if (!repeater || this.isLocked) { return; }
    this.control ? this.openInForm(null) : this.add.emit(repeater);
  }

  onEdit(row: CostRow): void {
    if (this.isLocked) { return; }
    this.control ? this.openInForm(row) : this.edit.emit(row);
  }

  onRemove(row: CostRow): void {
    if (!this.control) {
      this.remove.emit(row);
      return;
    }
    if (this.isLocked) { return; }
    ConfirmDialogComponent.open(this.matDialog, DialogType.REMOVE_EXPENSE, true)
      .afterClosed()
      .subscribe(confirmed => {
        const index = this.indexOf(row);
        // removeAt(-1) would silently drop the last cost.
        if (!confirmed || index < 0) { return; }
        this.control!.removeAt(index);
        this.control!.markAsDirty();
      });
  }

  /** Same dialog as the Nabava tab, but it hands the edited row back instead
   *  of patching, and the row is written into the form's FormArray. */
  private openInForm(row: CostRow | null): void {
    const repeater = row?.repeater ?? this.formRepeater();
    const index = row ? this.indexOf(row) : -1;
    if (row && index < 0) { return; }
    V2ExpenseFormDialogComponent.open(this.matDialog, null, null, repeater, row?.itemValue ?? null)
      .afterClosed()
      .subscribe(result => {
        if (!result?.value) { return; }
        const control = this.control!;
        if (index >= 0) {
          control.at(index).patchValue(result.value);
        } else {
          const item = this.formHelper.prepareEmptyRepeaterItem(repeater, true);
          item.patchValue(result.value);
          control.push(item);
        }
        control.markAsDirty();
        if (result.shouldReopen) { this.openInForm(null); }
      });
  }

  private indexOf(row: CostRow): number {
    return (row.repeater.value as unknown as any[]).indexOf(row.itemValue);
  }

  /** The repeater field with its `value` taken from the form control, in the
   *  `{wrappedItemUuid, value: [{fieldUuid, value}]}` shape the BE returns. */
  private formRepeater(): Field<string> {
    const value = (this.control!.getRawValue() as any[]).map(item => ({
      wrappedItemUuid: item.wrappedItemUuid,
      value: Object.entries(item.value ?? {}).map(([fieldUuid, v]) => ({fieldUuid, value: v})),
    }));
    return {...this.expenseRepeater!, value: value as any};
  }

  private buildRows(): void {
    const expenseRepeater = this.control && this.expenseRepeater ? this.formRepeater() : this.expenseRepeater;
    this.expenses = this.rowsFor(expenseRepeater, COST_REPEATERS.expense, true);
    this.expensesTotal = this.sumRows(this.expenses);
    this.incomes = this.rowsFor(this.incomeRepeater, COST_REPEATERS.income, false);
    this.incomesTotal = this.sumRows(this.incomes);
  }

  private rowsFor(
    repeater: Field<string> | null,
    ids: typeof COST_REPEATERS.expense | typeof COST_REPEATERS.income,
    isExpense: boolean,
  ): CostRow[] {
    if (!repeater?.value || !Array.isArray(repeater.value)) { return []; }
    const subFields = (repeater as any).config?.subFields;
    return (repeater.value as unknown as any[]).map((item: any, i: number): CostRow => ({
      itemValue: item,
      // Rows added in the form have no uuid until the vehicle is saved.
      uuid: item.uuid ?? item.itemUuid ?? item.wrappedItemUuid ?? `new-${i}`,
      name: this.fieldHelper.getValue(ids.title, subFields, item.value) ?? '',
      amount: Number(this.fieldHelper.getValue(ids.amount, subFields, item.value) ?? 0),
      date: this.fieldHelper.getValue(ids.date, subFields, item.value) ?? null,
      files: this.extractFileLinks(this.fieldHelper.getValue(ids.files, subFields, item.value)),
      comment: this.fieldHelper.getValue(ids.comment, subFields, item.value) ?? null,
      isExpense,
      repeater,
      invalid: isExpense && !!this.control?.at(i)?.invalid,
    }));
  }

  private sumRows(rows: CostRow[]): number {
    return rows.reduce((sum, r) => sum + (Number.isFinite(r.amount) ? r.amount : 0), 0);
  }

  /** Extract a human-readable filename from a URL/preview path. */
  private fileName(url: string | null): string {
    if (!url) { return $localize`Datoteka`; }
    const cleaned = url.split('?')[0];
    const segment = cleaned.split('/').pop() || $localize`Datoteka`;
    return decodeURIComponent(segment);
  }

  /**
   * `expense_files` items are Upload wrappers — `{uploadUuid, status, files: [{url, fileName, ...}]}`
   * — so the link lives one level down, not on the item itself.
   */
  private extractFileLinks(uploads: any): CostFileLink[] {
    if (!Array.isArray(uploads)) { return []; }
    return uploads
      .flatMap((upload: any) => Array.isArray(upload?.files) ? upload.files : [upload])
      .map((file: any) => {
        const url = file?.url ?? file?.preview ?? null;
        return url ? {url, fileName: file?.fileName ?? this.fileName(url)} : null;
      })
      .filter((f: CostFileLink | null): f is CostFileLink => f !== null);
  }

  /** Costs + incomes in one list, narrowed by the Filter dropdown and ordered
   *  by the clicked column — the legacy table's filter + matSort combined. */
  public visibleRows(): CostRow[] {
    let rows: CostRow[];
    switch (this.rowFilter) {
      case 'cost': rows = this.expenses; break;
      case 'income': rows = this.incomes; break;
      default: rows = [...this.expenses, ...this.incomes];
    }
    if (!this.sortColumn) { return rows; }

    const column = this.sortColumn;
    const direction = this.sortAsc ? 1 : -1;
    return [...rows].sort((a, b) => {
      const delta = column === 'amount'
        ? a.amount - b.amount
        : new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime();
      return delta * direction;
    });
  }

  /** Click cycles asc → desc → unsorted, matching matSort's default. */
  public toggleSort(column: 'amount' | 'date'): void {
    if (this.sortColumn !== column) {
      this.sortColumn = column;
      this.sortAsc = true;
    } else if (this.sortAsc) {
      this.sortAsc = false;
    } else {
      this.sortColumn = null;
    }
  }

  toggleDetails(): void { this.detailsOpen = !this.detailsOpen; }
}
